import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { placeCharm } from '@clasplab/look-stage';
import { LOOKS, BAND_ATTRS, COLOUR_GROUPS, WARDROBE, WHY, SETS, BAND_ONLY, EDITS, STATEMENT_EDITS } from '@clasplab/looks';

/**
 * "Find your WHOOP look" quiz (sections/cl-quiz.liquid), built from docs/briefs/2026-10-07-perfect-quiz.txt.
 * Four tap questions since 20 Oct (Cris), one per screen, tap = next: vibe (answered on the intro) →
 * wardrobe → how much detail (just the band / one small detail / a statement) → which WHOOP.
 * "What would you change" and "where" were dropped; old saved links that carry them still work
 * (ANSWER_KEYS), and "the way it feels" (change = feel) there still puts the result on Knit.
 * A short "Finding your look…" moment, then the result: the look's name, its photo, the band first and
 * the Claspie as a switch (off = band only), the price, Add my look, why we picked it, and the other
 * two looks that scored best for these answers.
 *
 * Engine settings live in assets/clasplab-looks.js. Order of work:
 *   1. Eligibility (hard filters): fit.models include the shopper's WHOOP (snippets/cl-compat); charms
 *      only on bands they're recorded to fit (band.kinds); only `wrist` bands get charms.
 *      "Not sure" doesn't block and doesn't guess: charms that fit every WHOOP model we list.
 *   2. Scoring within that set: the 8 looks; "just the band" → BAND_ONLY (Q3 picks the colour),
 *      a detail → the look's ready-made set (SETS), built from the catalogue only as a fallback.
 *      Sold-out sets / Edits / Claspies / band colours are never recommended (3 Nov): the next-best look
 *      with an in-stock set comes first, catalogue-built looks only fill up to three.
 * The result is saved in the browser (localStorage, 30 days) and in the URL (?look=…), and the main
 * look is handed to the builder (localStorage cl-look).
 */

const clTrack = (name, data) => {
  try {
    // @ts-ignore Shopify analytics global; custom events reach Customer Events pixels
    window.Shopify?.analytics?.publish?.(name, data);
  } catch (e) {}
};

/** The questions shown, in order (one screen each) */
const QUESTIONS = ['vibe', 'wardrobe', 'detail', 'model'];
/** Saved result format (?look=… and localStorage): all six answer slots, so links from before 20 Oct
 *  keep working; change and where stay blank for new results. */
const ANSWER_KEYS = ['vibe', 'change', 'where', 'wardrobe', 'detail', 'model'];

/** Q4 answers. Saved links from before 13 Oct carry a charm handle there: read as "one small detail". */
const DETAILS = ['none', 'small', 'statement'];
/** @param {Record<string, string>} answers */
const normalise = (answers) => {
  if (answers.detail && !DETAILS.includes(answers.detail)) answers.detail = 'small';
  return answers;
};

/** Quiz answer → value in the products' fit.models metafield */
const MODEL_VALUES = { '4.0': '4.0', '5.0': '5.0', mg: 'MG' };
const MODEL_LABELS = { '4.0': '4.0', '5.0': '5.0', mg: 'MG' };
const FIT_KEYS = { '5.0': 'whoop-5-0', mg: 'whoop-mg', '4.0': 'whoop-4-0' };

/** "The way it feels" → this band family (Cris, 15 Oct: our softest band) */
const KNIT = 'knit';

const STORE_KEY = 'cl-quiz';
/** In-progress answers for this tab (refresh / browser back keep the place); cleared on a result */
const PROGRESS_KEY = 'cl-quiz-progress-v2'; // v2 = the 4-question flow (20 Oct): a 6-question place restarts
/** Tap → next question: a short beat so the selected state registers */
const ADVANCE_MS = 220;
const STORE_DAYS = 30;
/** Saved answers are joined with "~" (handles contain "-", models contain ".") */
const SEP = '~';

const LOADING_LINES = ['Finding your look…', 'Matching your vibe…', 'Almost there…'];

/** @param {string[]} list */
const words = (list) => (list.length < 2 ? list.join('') : `${list.slice(0, -1).join(', ')} and ${list[list.length - 1]}`);

class ClaspLabQuiz extends HTMLElement {
  /** @type {{bands: any[], charms: any[]}} */
  #data = { bands: [], charms: [] };
  /** @type {Record<string, string>} */
  #answers = {};
  #step = 0;
  /** The three looks: your match and the next two best for these answers. @type {any[]} */
  #looks = [];
  /** Which look is shown as the result ("See look" switches it) */
  #current = 0;
  /** The Claspie switch on the result: off = the band alone (only for looks with a band and a Claspie) */
  #withCharm = true;
  /** How the result was opened ('quiz', 'saved', 'link', 'model', 'swap'), for the eyebrow */
  #source = 'quiz';

  get #main() {
    return this.#looks[this.#current];
  }

  /** The look as it will be bought: without the Claspie (and its set photo) when it's switched off */
  get #shown() {
    const main = this.#main;
    if (!main || this.#withCharm || !main.band || !main.charm) return main;
    return { ...main, charm: null, charms: null, set: null, photo: '', photoSmall: '' };
  }
  #busy = false;
  /** Set while a tap is advancing, so a double tap can't skip a question */
  #advancing = false;

  connectedCallback() {
    try {
      this.#data = JSON.parse(this.querySelector('[data-quiz-data]')?.textContent || '{}');
    } catch (e) {
      console.error('Quiz data', e);
    }
    this.addEventListener('click', this.#onClick);
    window.addEventListener('popstate', this.#onPop);
    this.#warmImages();
    this.#restore();
    this.#watchSticky();
  }

  /** Question photos load lazily (they're hidden at first, so they don't compete with the first
   *  screen), then load in the background once the page is ready, so every tap feels instant. */
  #warmImages() {
    const warm = () =>
      this.querySelectorAll('[data-q] img[loading="lazy"]').forEach((img) => img.setAttribute('loading', 'eager'));
    const idle = () => ('requestIdleCallback' in window ? window.requestIdleCallback(warm, { timeout: 2500 }) : setTimeout(warm, 1200));
    if (document.readyState === 'complete') idle();
    else window.addEventListener('load', idle, { once: true });
    // Starting the quiz before that (a tap on an intro answer): load them now
    this.querySelector('[data-screen="intro"]')?.addEventListener('pointerdown', warm, { once: true });
  }

  /* ---------- Saved result ---------- */

  #encode() {
    return ANSWER_KEYS.map((k) => this.#answers[k] || '').join(SEP);
  }

  /** @param {string} value */
  #decode(value) {
    const parts = (value || '').split(SEP);
    // Saved links from before 15 Oct have no "change" answer (5 parts): read it as blank
    if (parts.length === ANSWER_KEYS.length - 1) parts.splice(1, 0, '');
    if (parts.length !== ANSWER_KEYS.length || parts.some((p) => !/^[a-z0-9.-]*$/.test(p))) return null;
    /** @type {Record<string, string>} */
    const answers = {};
    ANSWER_KEYS.forEach((k, i) => {
      if (parts[i]) answers[k] = parts[i];
    });
    return answers.model && answers.vibe ? normalise(answers) : null;
  }

  /** A shared link (?look=…) wins; otherwise the last result from this browser (30 days). */
  #restore() {
    const linked = new URLSearchParams(window.location.search).get('look') || '';
    let saved = '';
    try {
      const data = JSON.parse(localStorage.getItem(STORE_KEY) || 'null');
      if (data && Date.now() - data.at < STORE_DAYS * 864e5) saved = data.look;
    } catch (e) {}
    let answers = this.#decode(linked);
    // A link to the shopper's own result (e.g. after a refresh) counts as their saved look
    const source = answers && linked !== saved ? 'link' : 'saved';
    if (!answers && this.#resumeProgress()) return;
    if (!answers) answers = this.#decode(saved);
    if (!answers) return;
    this.#answers = answers;
    this.#finish(source);
  }

  /** Mid-quiz refresh: back to the same question with the answers so far. @returns {boolean} */
  #resumeProgress() {
    let data = null;
    try {
      data = JSON.parse(sessionStorage.getItem(PROGRESS_KEY) || 'null');
    } catch (e) {}
    if (!data || typeof data.step !== 'number' || data.step < 0 || data.step >= QUESTIONS.length) return false;
    /** @type {Record<string, string>} */
    const answers = {};
    ANSWER_KEYS.forEach((k) => {
      const v = data.answers?.[k];
      if (typeof v === 'string' && /^[a-z0-9.-]+$/.test(v)) answers[k] = v;
    });
    this.#answers = normalise(answers);
    this.#showQuestion(data.step);
    window.history.replaceState({ ...(window.history.state || {}), clQuiz: data.step }, '');
    return true;
  }

  #saveProgress() {
    try {
      sessionStorage.setItem(PROGRESS_KEY, JSON.stringify({ step: this.#step, answers: this.#answers }));
    } catch (e) {}
  }

  #clearProgress() {
    try {
      sessionStorage.removeItem(PROGRESS_KEY);
    } catch (e) {}
  }

  /** Question screens are browser history entries, so the phone's back gesture goes back a question */
  /** @param {number} step */
  #go(step) {
    this.#showQuestion(step);
    window.history.pushState({ ...(window.history.state || {}), clQuiz: step }, '');
  }

  /** @param {PopStateEvent} event */
  #onPop = (event) => {
    const at = event.state?.clQuiz;
    if (typeof at === 'number') this.#showQuestion(at);
    else if (at === 'result' && this.#looks.length) this.#screen('result');
    else this.#screen('intro');
  };

  #save() {
    try {
      localStorage.setItem(STORE_KEY, JSON.stringify({ look: this.#encode(), at: Date.now() }));
    } catch (e) {}
    const url = new URL(window.location.href);
    url.searchParams.set('look', this.#encode());
    window.history.replaceState(window.history.state, '', url);
  }

  #forget() {
    try {
      localStorage.removeItem(STORE_KEY);
    } catch (e) {}
    const url = new URL(window.location.href);
    url.searchParams.delete('look');
    window.history.replaceState(window.history.state, '', url);
  }

  /** Phones: the sticky "Add my look" bar shows while the main button is out of view. */
  #watchSticky() {
    const main = this.querySelector('[data-main-add]');
    const bar = this.querySelector('[data-r="sticky"]');
    if (!(main instanceof HTMLElement) || !(bar instanceof HTMLElement) || !('IntersectionObserver' in window)) return;
    new IntersectionObserver(([entry]) => {
      const result = this.querySelector('[data-screen="result"]');
      const show = !entry.isIntersecting && entry.boundingClientRect.top > 0 && result instanceof HTMLElement && !result.hidden;
      bar.dataset.visible = String(show);
    }).observe(main);
  }

  /* ---------- Screens ---------- */

  /** @param {string} name */
  #screen(name) {
    this.querySelectorAll('[data-screen]').forEach((el) => {
      if (el instanceof HTMLElement) el.hidden = el.dataset.screen !== name;
    });
    // The intro is question one: show the answer picked before (back from question two)
    if (name === 'intro') {
      this.querySelectorAll('[data-intro-vibe]').forEach((b) =>
        b.setAttribute('aria-pressed', String(b.getAttribute('data-intro-vibe') === this.#answers.vibe))
      );
    }
    const top = this.getBoundingClientRect().top + window.scrollY - 100;
    if (window.scrollY > top) window.scrollTo({ top, behavior: 'smooth' });
  }

  /** @param {number} step */
  #showQuestion(step) {
    this.#step = step;
    this.#screen('q');
    const key = QUESTIONS[step];
    this.querySelectorAll('[data-q]').forEach((el) => {
      if (!(el instanceof HTMLElement)) return;
      el.hidden = el.dataset.q !== key;
      if (el.dataset.q === key) {
        el.querySelectorAll('[data-value]').forEach((b) =>
          b.setAttribute('aria-pressed', String(b.getAttribute('data-value') === this.#answers[key]))
        );
      }
    });
    const help = this.querySelector('[data-unsure-help]');
    if (help instanceof HTMLElement) help.hidden = true;
    this.querySelectorAll('.cl-quiz__dot').forEach((d, i) => d.classList.toggle('is-done', i <= step));
    const pad = (/** @type {number} */ n) => String(n).padStart(2, '0');
    const count = this.querySelector('[data-count]');
    if (count) count.textContent = `${pad(step + 1)} / ${pad(QUESTIONS.length)}`;
    const bar = this.querySelector('[data-progress]');
    if (bar instanceof HTMLElement) bar.style.setProperty('--p', String((step + 1) / QUESTIONS.length));
    const live = this.querySelector('[data-progress-live]');
    if (live) live.textContent = `Question ${step + 1} of ${QUESTIONS.length}`;
    this.#saveProgress();
    // Focus the question (not an option, which would look pre-selected)
    const title = this.querySelector(`[data-q="${key}"] .cl-quiz__q-title`);
    if (title instanceof HTMLElement) {
      title.tabIndex = -1;
      title.focus({ preventScroll: true });
    }
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const t = event.target instanceof Element ? event.target : null;
    if (!t) return;
    // Intro = question one (Cris, 15 Oct): a tap answers it and goes straight to question two
    const introVibe = t.closest('[data-intro-vibe]');
    if (introVibe instanceof HTMLElement) {
      if (this.#advancing) return;
      this.#advancing = true;
      this.#answers = { ...this.#answers, vibe: introVibe.dataset.introVibe || '' };
      this.querySelectorAll('[data-intro-vibe]').forEach((b) => b.setAttribute('aria-pressed', String(b === introVibe)));
      clTrack('quiz_started', { from: 'intro' });
      clTrack('quiz_question_answered', { question: 'vibe', step: 1, answer: this.#answers.vibe });
      window.setTimeout(() => {
        this.#advancing = false;
        this.#go(1);
      }, ADVANCE_MS);
      return;
    }
    if (t.closest('[data-start]')) {
      clTrack('quiz_started', {});
      this.#go(0);
      return;
    }
    if (t.closest('[data-restart]')) {
      this.#answers = {};
      this.#advancing = false;
      this.#forget();
      this.#go(0);
      return;
    }
    if (t.closest('[data-back]')) {
      // Same as the browser's back when we put the question in history; otherwise step back here
      if (typeof window.history.state?.clQuiz === 'number') window.history.back();
      else if (this.#step === 0) this.#screen('intro');
      else this.#showQuestion(this.#step - 1);
      return;
    }
    // "I'm not sure": help, no dead end
    if (t.closest('[data-unsure]')) {
      const help = this.querySelector('[data-unsure-help]');
      if (help instanceof HTMLElement) {
        help.hidden = false;
        help.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
      t.closest('[data-unsure]')?.setAttribute('aria-pressed', 'true');
      return;
    }
    // Result for "not sure": naming the WHOOP re-runs the result with bands (no 5.0 default)
    const pick = t.closest('[data-pick-model]');
    if (pick instanceof HTMLElement && pick.dataset.pickModel) {
      this.#answers.model = pick.dataset.pickModel;
      clTrack('quiz_question_answered', { question: 'model', step: QUESTIONS.length, answer: this.#answers.model, from: 'result' });
      this.#finish('model');
      this.querySelector('[data-screen="result"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    if (t.closest('[data-unsure-go]')) {
      this.#answers.model = 'unsure';
      clTrack('quiz_question_answered', { question: 'model', step: QUESTIONS.length, answer: 'unsure' });
      this.#finish('quiz');
      return;
    }
    const opt = t.closest('[data-q] [data-value]');
    if (opt instanceof HTMLElement) {
      if (this.#advancing) return;
      this.#advancing = true;
      const key = QUESTIONS[this.#step];
      this.#answers[key] = opt.dataset.value || '';
      opt.closest('[data-q]')?.querySelectorAll('[data-value]').forEach((b) => b.setAttribute('aria-pressed', String(b === opt)));
      clTrack('quiz_question_answered', { question: key, step: this.#step + 1, answer: this.#answers[key] });
      // A short beat so the tap registers visually
      window.setTimeout(() => {
        this.#advancing = false;
        if (this.#step < QUESTIONS.length - 1) this.#go(this.#step + 1);
        else this.#finish('quiz');
      }, ADVANCE_MS);
      return;
    }
    if (t.closest('[data-charm-toggle]')) {
      this.#withCharm = !this.#withCharm;
      clTrack('quiz_charm_toggled', { look: this.#lookInfo().name, on: this.#withCharm });
      this.#renderResult(this.#source, true);
      const again = this.querySelector('[data-charm-toggle]');
      if (again instanceof HTMLElement) again.focus();
      return;
    }
    // "Not quite your taste?" → the Sets collection (the link itself navigates)
    if (t.closest('[data-sets-link]')) clTrack('quiz_sets_clicked', { look: this.#lookInfo().name });
    const seeLook = t.closest('[data-see-look]');
    if (seeLook instanceof HTMLElement) {
      this.#selectLook(Number(seeLook.dataset.seeLook));
      return;
    }
    const product = t.closest('[data-product-link]');
    if (product instanceof HTMLElement) {
      clTrack('recommended_product_clicked', { kind: product.dataset.kind, handle: product.dataset.handle });
    }
    if (t.closest('[data-shop-look]')) {
      const buy = this.querySelector('[data-main-add]');
      if (buy instanceof HTMLElement) window.scrollTo({ top: buy.getBoundingClientRect().top + window.scrollY - 220, behavior: 'smooth' });
      return;
    }
    const add = t.closest('[data-add]');
    if (add instanceof HTMLElement) this.#add(add.dataset.add === 'main' ? this.#current : Number(add.dataset.add), add);
    const share = t.closest('[data-share]');
    if (share instanceof HTMLElement) this.#share(share);
  };

  /* ---------- Eligibility (hard filters) ---------- */

  /** @param {any} product */
  #fitsModel(product) {
    const models = product.models || [];
    const model = MODEL_VALUES[this.#answers.model];
    // Not sure: only what fits every model we list, so it works whichever WHOOP they have
    if (!model) return Object.values(MODEL_VALUES).every((m) => models.includes(m));
    return models.includes(model);
  }

  #eligibleBands() {
    if (!MODEL_VALUES[this.#answers.model]) return [];
    return this.#data.bands.filter((b) => b.wrist && this.#fitsModel(b) && b.variants.some((/** @type {any} */ v) => v.available));
  }

  #eligibleCharms() {
    return this.#data.charms.filter((c) => c.available && !(c.tags || []).includes('set-of-3') && this.#fitsModel(c));
  }

  /* ---------- Scoring ---------- */

  /** @param {any} charm */
  #finishOf(charm) {
    const tags = charm?.tags || [];
    if (tags.includes('crystal')) return 'crystal';
    if (tags.includes('gold')) return 'gold';
    if (tags.includes('silver')) return 'silver';
    return 'other';
  }

  /**
   * Looks ranked by the answers, highest first. Ties (deterministic): the look that matches the
   * vibe (Q1) wins, then the one that matches where (Q2), then the Everyday Icon, then LOOKS order.
   */
  #rankLooks() {
    const a = this.#answers;
    return Object.entries(LOOKS)
      .map(([key, look], i) => {
        const s = look.score;
        const score = (s.vibe?.[a.vibe] || 0) + (s.change?.[a.change] || 0) + (s.where?.[a.where] || 0) + (s.wardrobe?.[a.wardrobe] || 0) + (s.detail?.[a.detail] || 0);
        return { key, i, score, vibe: (s.vibe?.[a.vibe] || 0) > 0, where: (s.where?.[a.where] || 0) > 0 };
      })
      .sort(
        (x, y) =>
          y.score - x.score ||
          Number(y.vibe) - Number(x.vibe) ||
          Number(y.where) - Number(x.where) ||
          Number(y.key === 'everyday') - Number(x.key === 'everyday') ||
          x.i - y.i
      )
      .map((x) => x.key);
  }

  /** @param {any} band @param {Record<string, number>} prefs */
  #bandScore(band, prefs) {
    const a = this.#answers;
    const attrs = BAND_ATTRS[band.handle] || { vibe: {}, where: {} };
    return (prefs[band.handle] || 0) * 2 + (attrs.vibe[a.vibe] || 0) + (attrs.where[a.where] || 0);
  }

  /** @param {any} band @param {Record<string, number>} colourPrefs */
  #bestVariant(band, colourPrefs) {
    const wardrobe = WARDROBE[this.#answers.wardrobe]?.colour || {};
    return band.variants
      .filter((/** @type {any} */ v) => v.available)
      .map((/** @type {any} */ v, /** @type {number} */ i) => {
        const groups = COLOUR_GROUPS[v.title] || [];
        const score = groups.reduce((sum, g) => sum + (wardrobe[g] || 0) + (colourPrefs[g] || 0), 0);
        return { v, score: score - i * 0.01 };
      })
      .sort((/** @type {any} */ x, /** @type {any} */ y) => y.score - x.score)[0]?.v;
  }

  /** @param {any} charm @param {Record<string, number>} prefs */
  #charmScore(charm, prefs) {
    const tags = charm.tags || [];
    const wardrobe = WARDROBE[this.#answers.wardrobe]?.charm || {};
    const points = (/** @type {Record<string, number>} */ table) =>
      Object.entries(table).reduce((sum, [tag, n]) => sum + (tags.includes(tag) ? n : 0), 0);
    let score = points(prefs) + points(wardrobe);
    if (this.#answers.detail === 'statement') score += points({ crystal: 2, clip: 1 });
    if (charm.cutout) score += 1; // can be shown on the band
    return score;
  }

  /**
   * One look: the best eligible band (+ colour) for these preferences, and a charm that fits it.
   * @param {string} key - look in LOOKS
   * @param {any} prefs - { band / bands, colour, charm } preferences
   * @param {{ bands: any[], charms: any[], used: Set<number>, label?: string, avoidBand?: string }} ctx
   */
  #buildLook(key, prefs, ctx) {
    const info = LOOKS[key];
    let band = null;
    if (ctx.bands.length) {
      const bandPrefs = prefs.bands || prefs.band || {};
      const b = ctx.bands
        .map((x, i) => ({ x, s: this.#bandScore(x, bandPrefs) - (x.handle === ctx.avoidBand ? 5 : 0) - i * 0.001 }))
        .sort((p, q) => q.s - p.s)[0].x;
      const variant = this.#bestVariant(b, prefs.colour || info.colour || {});
      if (variant) band = { band: b, variant };
    }
    // Charms that fit this band (recorded pairings), or every eligible charm without a band
    const pool = band ? ctx.charms.filter((c) => (band.band.kinds || []).includes(c.kind)) : ctx.charms;
    const charm =
      pool
        .filter((c) => !ctx.used.has(c.id))
        .map((c, i) => ({ c, s: this.#charmScore(c, prefs.charm || {}) - i * 0.001 }))
        .sort((p, q) => q.s - p.s)[0]?.c || null;
    if (charm) ctx.used.add(charm.id);
    return { key, info: { name: info.name, line: info.line }, label: ctx.label || '', band, charm };
  }

  /**
   * The look's ready-made set (SETS in clasplab-looks.js) with its studio photo, when every part is
   * available and fits the shopper's WHOOP. MG / 4.0 / not sure: the set's charm only (charm photo).
   * Returns null when the set can't be used, so the caller builds the look from the catalogue instead.
   * @param {string} key
   * @param {{ bands: any[], charms: any[], used: Set<number>, label?: string }} ctx
   */
  #buildSet(key, ctx) {
    const set = SETS[key];
    // Studio photo when there is one; otherwise the result shows band + Claspie side by side (14 Oct)
    const photos = this.#data.setPhotos?.[key] || {};
    if (!set) return null;
    // Sold-out set (Cris, 3 Nov: stop selling at 0): never recommended; #finish moves on to the next-best look
    if (ctx.bands.length && photos.soldOut) return null;
    const charm = ctx.charms.find((c) => c.handle === set.charm);
    if (!charm || ctx.used.has(charm.id)) return null;
    let band = null;
    if (ctx.bands.length) {
      const b = ctx.bands.find((x) => x.handle === set.band);
      const variant = b?.variants.find((/** @type {any} */ v) => v.title === set.colour && v.available);
      if (!b || !variant || !(b.kinds || []).includes(charm.kind)) return null;
      band = { band: b, variant };
    }
    ctx.used.add(charm.id);
    const info = LOOKS[key];
    return {
      key,
      info: { name: info.name, line: info.line },
      label: ctx.label || '',
      band,
      charm,
      // Band + Claspie = the live set product: bought as the set, at the set price (Cris, 15 Oct)
      set: band && photos.product ? photos.product : null,
      photo: (band ? photos.set : photos.charm) || '',
      photoSmall: (band ? photos.setSmall : photos.charmSmall) || '',
    };
  }

  /**
   * "Make it a statement" on WHOOP 5.0 (Cris, 20 Oct): the look's first Edit (STATEMENT_EDITS) that is live, in
   * stock-or-selling, fits the band and isn't already in this result. look.charm = its first Claspie (so every
   * single-Claspie path keeps working), look.charms = all of them. Returns null otherwise (the caller falls back).
   * @param {string} key
   * @param {{ bands: any[], charms: any[], used: Set<number>, usedEdits: Set<string>, label?: string }} ctx
   */
  #buildEdit(key, ctx) {
    if (this.#answers.detail !== 'statement' || !ctx.bands.length) return null;
    for (const name of STATEMENT_EDITS[key] || []) {
      const edit = EDITS[name];
      const photos = this.#data.editPhotos?.[name] || {};
      if (!edit || ctx.usedEdits.has(name) || photos.soldOut || !photos.product || !photos.set) continue;
      const b = ctx.bands.find((x) => x.handle === edit.band);
      const variant = b?.variants.find((/** @type {any} */ v) => v.title === edit.colour && v.available);
      const charms = edit.charms.map((h) => ctx.charms.find((c) => c.handle === h));
      if (!b || !variant || charms.some((c) => !c || !(b.kinds || []).includes(c.kind))) continue;
      ctx.usedEdits.add(name);
      charms.forEach((c) => ctx.used.add(c.id));
      const info = LOOKS[key];
      return {
        key,
        info: { name: info.name, line: info.line },
        label: ctx.label || '',
        band: { band: b, variant },
        charm: charms[0],
        charms,
        set: photos.product,
        photo: photos.set,
        photoSmall: photos.setSmall || photos.set,
      };
    }
    return null;
  }

  /**
   * "Nothing, just the band": the look's band-only result (BAND_ONLY in clasplab-looks.js), in the
   * colour that best matches the wardrobe answer. Shown with the band's own photo; no Claspie.
   * Returns null when none of its bands / colours is available, so the caller can fall back.
   * @param {string} key
   * @param {{ bands: any[], label?: string }} ctx
   * @param {Set<string>} taken - band + colour already in another result
   */
  #buildBandOnly(key, ctx, taken) {
    const wardrobe = WARDROBE[this.#answers.wardrobe]?.colour || {};
    const options = (BAND_ONLY[key] || [])
      .map(([handle, colour], i) => {
        const band = ctx.bands.find((b) => b.handle === handle);
        const variant = band?.variants.find((/** @type {any} */ v) => v.title === colour && v.available);
        const groups = COLOUR_GROUPS[colour] || [];
        const score = groups.reduce((sum, g) => sum + (wardrobe[g] || 0), 0) + (i === 0 ? 0.5 : 0) - i * 0.01;
        return { band, variant, score };
      })
      .filter((o) => o.band && o.variant && !taken.has(`${o.band.handle}/${o.variant.title}`))
      .sort((x, y) => y.score - x.score);
    const best = options[0];
    if (!best) return null;
    const info = LOOKS[key];
    return { key, info: { name: info.name, line: info.line }, label: ctx.label || '', band: { band: best.band, variant: best.variant }, charm: null };
  }

  /**
   * "The way it feels on my wrist" (Cris, 15 Oct: Knit is the most comfortable band): the look moves to
   * Knit in the colour closest to the look and the wardrobe answer, keeping its Claspie. A set photo
   * only stays when the set already is Knit; otherwise band + Claspie show side by side.
   * @param {any} look @param {{ bands: any[] }} ctx @param {Set<string>} taken
   */
  #onKnit(look, ctx, taken) {
    if (!look.band || look.band.band.handle === KNIT) return;
    const knit = ctx.bands.find((b) => b.handle === KNIT);
    if (!knit || (look.charm && !(knit.kinds || []).includes(look.charm.kind))) return;
    const free = { ...knit, variants: knit.variants.filter((/** @type {any} */ v) => !taken.has(`${KNIT}/${v.title}`)) };
    const variant = this.#bestVariant(free, LOOKS[look.key]?.colour || {});
    if (!variant) return;
    look.band = { band: knit, variant };
    look.set = null;
    look.photo = '';
    look.photoSmall = '';
  }

  /** @param {string} source - 'quiz' (just answered), 'saved' (this browser), 'link' (?look=) or 'model' (picked on the result) */
  #finish(source) {
    const a = this.#answers;
    if (!a.model) return;
    const ranked = this.#rankLooks();
    const ctx = { bands: this.#eligibleBands(), charms: this.#eligibleCharms(), used: new Set(), usedEdits: new Set() };
    // "Just the band" needs a band for their WHOOP; MG / 4.0 / not sure get a Claspie instead
    const bandOnly = a.detail === 'none' && ctx.bands.length > 0;
    /** @type {Set<string>} band + colour already shown, so the three looks never repeat */
    const taken = new Set();
    /** @type {any[]} */
    const looks = [];
    /** @type {Set<string>} looks already in the result */
    const done = new Set();
    // Pass 1: band only, or the ready-made set / Edit with its studio photo (in stock: a sold-out set is skipped and
    // the next-best look takes its place; Cris, 3 Nov). Pass 2: only when fewer than three, the remaining looks
    // built from the in-stock catalogue.
    for (const pass of [1, 2]) {
      for (const key of ranked) {
        if (looks.length === 3) break;
        if (done.has(key)) continue;
        const label = looks.length ? LOOKS[key].name : 'Your match';
        const look =
          (bandOnly && this.#buildBandOnly(key, { ...ctx, label }, taken)) ||
          (!bandOnly && this.#buildEdit(key, { ...ctx, label })) ||
          (!bandOnly && this.#buildSet(key, { ...ctx, label })) ||
          (pass === 2 && !bandOnly && this.#buildLook(key, LOOKS[key], { ...ctx, label, avoidBand: looks[0]?.band?.band.handle })) ||
          null;
        if (!look || !(look.band || look.charm)) continue;
        if (a.change === 'feel') this.#onKnit(look, ctx, taken);
        const id = look.band ? `${look.band.band.handle}/${look.band.variant.title}` : '';
        if (id && taken.has(id) && !look.charm) continue;
        if (id) taken.add(id);
        done.add(key);
        looks.push(look);
      }
    }
    this.#looks = looks;
    this.#current = 0;
    this.#withCharm = true;
    if (!this.#looks.length) return;
    const main = this.#looks[0];

    const show = () => {
      this.#renderResult(source);
      // Someone else's shared look is shown, but doesn't replace this browser's own saved result
      if (source !== 'link') this.#save();
      try {
        if (FIT_KEYS[a.model]) localStorage.setItem('cl-whoop-model', FIT_KEYS[a.model]);
      } catch (e) {}
      clTrack('quiz_result_viewed', { look: main.info.name, source, model: a.model });
      if (source === 'quiz') {
        clTrack('quiz_completed', {
          vibe: a.vibe,
          change: a.change,
          where: a.where,
          wardrobe: a.wardrobe,
          detail: a.detail,
          model: a.model,
          look: main.info.name,
        });
      }
    };
    this.#clearProgress();
    if (source === 'quiz') {
      window.history.pushState({ ...(window.history.state || {}), clQuiz: 'result' }, '');
      this.#loading(show);
    } else show();
  }

  /** "Finding your look… / Matching your vibe… / Almost there…", about a second */
  /** @param {() => void} done */
  #loading(done) {
    this.#screen('loading');
    const line = this.querySelector('[data-loading-line]');
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const step = reduced ? 120 : 380;
    LOADING_LINES.forEach((text, i) =>
      window.setTimeout(() => {
        if (line) line.textContent = text;
      }, i * step)
    );
    window.setTimeout(done, LOADING_LINES.length * step);
  }

  /* ---------- Result ---------- */

  #lookInfo() {
    return this.#main?.info || { name: '', line: '' };
  }

  /** "Heart", from "Charm Bar · Red Heart" / "Charm Clip · Pink Heart, gold" */
  /** @param {any} charm */
  #charmShort(charm) {
    return (charm?.title || '').split(' · ').pop().replace(/,\s*(gold|silver)$/i, '');
  }

  /** @param {any} charm */
  #finishLabel(charm) {
    return { gold: 'Gold', silver: 'Silver', crystal: 'Crystal', other: '' }[this.#finishOf(charm)];
  }

  /** Result rows, as in the brief: "Knit — Bone", "Heart — Gold" */
  /** @param {any} look */
  #bandName(look) {
    return `${look.band.band.title} — ${look.band.variant.title}`;
  }

  /** @param {any} charm */
  #charmName(charm) {
    const finish = this.#finishLabel(charm);
    return finish ? `${this.#charmShort(charm)} — ${finish}` : this.#charmShort(charm);
  }

  /** Direction cards: "Bone Knit + Gold Heart" */
  /** @param {any} look */
  #comboName(look) {
    const parts = [];
    if (look.band) parts.push(`${look.band.variant.title} ${look.band.band.title}`);
    const charms = this.#charmsOf(look);
    if (charms.length > 1) parts.push(`${charms.length} Claspies`);
    else if (look.charm) parts.push(`${this.#finishLabel(look.charm)} ${this.#charmShort(look.charm)}`.trim());
    return parts.join(' + ');
  }

  /** @param {number} cents */
  #money(cents) {
    return new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency: this.dataset.currency || 'EUR',
      minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
    }).format(cents / 100);
  }

  /** @param {string} text */
  #esc(text) {
    const d = document.createElement('div');
    d.textContent = text;
    return d.innerHTML;
  }

  /** @param {any} look */
  #total(look) {
    if (look.set && look.band && look.charm) return look.set.price;
    return this.#partsTotal(look);
  }

  /** Every Claspie in the look (an Edit has two or three) @param {any} look @returns {any[]} */
  #charmsOf(look) {
    return look.charms?.length ? look.charms : look.charm ? [look.charm] : [];
  }

  /** "Pink Butterfly, Pink Heart and Pink Donut" @param {any} look */
  #charmList(look) {
    const names = this.#charmsOf(look).map((c) => this.#charmShort(c).replace(/,\s*light$/i, ''));
    return names.length > 1 ? `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}` : names[0] || '';
  }

  /** Band + Claspies bought separately (what the set saves against) @param {any} look */
  #partsTotal(look) {
    return (look.band ? look.band.variant.price : 0) + this.#charmsOf(look).reduce((sum, c) => sum + c.price, 0);
  }

  /** @param {any} look */
  #names(look) {
    const parts = [];
    if (look.band) parts.push(`${look.band.band.title} · ${look.band.variant.title}`);
    this.#charmsOf(look).forEach((c) => parts.push(c.title));
    return parts;
  }

  /** Product page link that keeps the look: the band's colour and the charm (bundle-tiers preselects it) */
  /** @param {any} look */
  #bandUrl(look) {
    const q = new URLSearchParams({ variant: String(look.band.variant.id) });
    if (look.charm) q.set('charm', look.charm.handle);
    // Band families: each colour has its own page (variant.url); the variant id still preselects it
    return `${look.band.variant.url || look.band.band.url}?${q}`;
  }

  /**
   * The look as one picture: band photo + charm cut-out at the band's placement (same as the
   * builder). Charm only: the charm photo. Band only: the band photo.
   * @param {any} look
   * @param {boolean} [small]
   */
  #visualHtml(look, small = false) {
    const band = look.band;
    const charm = look.charm;
    const loading = small ? 'lazy' : 'eager';
    // Ready-made set: its studio photo
    if (look.photo) {
      const with_ = this.#charmsOf(look).length > 1 ? this.#charmList(look) : charm.title;
      const alt = band ? `${band.band.title} · ${band.variant.title} with ${with_}` : `${charm.title} on an original WHOOP band`;
      return `<div class="cl-quiz__photo"><img src="${small ? look.photoSmall || look.photo : look.photo}" alt="${this.#esc(alt)}" width="1200" height="1200" loading="${loading}"></div>`;
    }
    if (band && band.band.place && charm?.cutout) {
      return `<div class="cl-stage" data-stage>
          <img class="cl-stage__band" src="${band.variant.stage || band.variant.image}" alt="${this.#esc(`${band.band.title} · ${band.variant.title} with ${charm.title}`)}" width="1000" height="1000" loading="${loading}">
          <img class="cl-stage__charm" src="${charm.cutout}" alt="" loading="${loading}">
        </div>`;
    }
    if (!band) return `<div class="cl-quiz__solo cl-quiz__solo--charm"><img src="${charm.image}" alt="${this.#esc(charm.title)}" loading="${loading}"></div>`;
    if (!charm) {
      return `<div class="cl-quiz__solo"><img src="${band.variant.stage || band.variant.image}" alt="${this.#esc(`${band.band.title} · ${band.variant.title}`)}" loading="${loading}"></div>`;
    }
    return `<div class="cl-quiz__pair"><img src="${band.variant.image}" alt="${this.#esc(`${band.band.title} · ${band.variant.title}`)}" loading="${loading}"><img src="${charm.image}" alt="${this.#esc(charm.title)}" loading="${loading}"></div>`;
  }

  /** Put each drawn charm where its band says (after the HTML is in the page) */
  /** @param {Element} root @param {any[]} looks */
  #placeStages(root, looks) {
    const staged = looks.filter((l) => !l.photo && l.band && l.band.band.place && l.charm?.cutout);
    root.querySelectorAll('[data-stage]').forEach((stage, n) => {
      const look = staged[n];
      const img = stage.querySelector('.cl-stage__charm');
      if (look && img instanceof HTMLImageElement) placeCharm(img, look.band.band.place, look.charm.kind);
    });
  }

  /** @param {any} look */
  #isPreorder(look) {
    if (look?.set && look.band && look.charm) return Boolean(look.set.preorder);
    return Boolean(look?.band?.band.preorder || look?.charm?.preorder);
  }

  /** "Why we picked it": built from the answers and the products in the look, style words only */
  /** @param {any} look */
  #why(look) {
    const a = this.#answers;
    // "where" is only in saved links from before 20 Oct
    const start = a.where
      ? `You gravitated toward ${WHY.vibe[a.vibe] || 'your own style'} and said you wear your WHOOP ${WHY.where[a.where] || 'everywhere'}.`
      : `You gravitated toward ${WHY.vibe[a.vibe] || 'your own style'}.`;
    const many = this.#charmsOf(look).length > 1;
    const charm = many ? this.#charmList(look) : look.charm ? `${this.#finishLabel(look.charm)} ${this.#charmShort(look.charm)}`.trim() : '';
    const charmLine = WHY.detail[a.detail] || WHY.detail.small;
    if (look.band) {
      const goes = WHY.wardrobe[a.wardrobe];
      const band = `The ${look.band.variant.title} ${look.band.band.title} ${WHY.band[look.key] || 'keeps it easy'}${goes ? ` and goes with ${goes}` : ''}.`;
      // What they wanted to change, when the look answers it (Knit for comfort, more looks with a Claspie)
      const extra = a.change === 'feel' && look.band.band.handle === KNIT ? ` ${WHY.change.feel}` : a.change === 'switch' && look.charm ? ` ${WHY.change.switch}` : '';
      if (look.charm && many) return `${start} ${band}${extra} The ${charm} are your statement.`;
      if (look.charm) return `${start} ${band}${extra} The ${charm} ${charmLine}.`;
      return `${start} ${band}${extra} ${a.detail === 'none' ? WHY.none : WHY.off}`;
    }
    // MG / 4.0 / not sure: a Claspie for their original band (bands are for WHOOP 5.0 only)
    if (a.detail === 'none') return `${start} Your original WHOOP band stays. If you ever want one small detail, the ${charm} fits it.`;
    return `${start} Your original WHOOP band stays, and the ${charm} ${charmLine}.`;
  }

  /** Copy derived from the product data (fit.models), never hard-coded fit claims */
  #fitCopy() {
    const a = this.#answers;
    const label = MODEL_LABELS[a.model];
    const bandModels = [...new Set(this.#data.bands.filter((b) => b.wrist).flatMap((b) => b.models || []))];
    const charmModels = Object.values(MODEL_VALUES).filter((m) => this.#data.charms.some((c) => (c.models || []).includes(m)));
    const hasBand = this.#looks.some((l) => l.band);
    if (!label) {
      return {
        note: `Not sure which WHOOP you have? So we've picked Claspies only: they fit the original WHOOP ${words(charmModels)} bands.${bandModels.length ? ` Our bands are for WHOOP ${words(bandModels)}, so we'll suggest one once we know your model.` : ''}`,
        fit: `Fits the original WHOOP ${words(charmModels)} bands`,
      };
    }
    if (!hasBand) {
      if (a.detail === 'none' && bandModels.length) {
        return {
          note: `You asked for just the band. Our bands are made for WHOOP ${words(bandModels)} only, so there's no band for your WHOOP ${label} yet. Here's a Claspie for your original band instead.`,
          fit: `Fits your original WHOOP ${label} band`,
        };
      }
      return {
        note: bandModels.length
          ? `Our bands are made for WHOOP ${words(bandModels)}, so your look is built around a Claspie for your original WHOOP ${label} band.`
          : `Your look is built around a Claspie for your original WHOOP ${label} band.`,
        fit: `Fits your original WHOOP ${label} band`,
      };
    }
    return { note: '', fit: '' };
  }

  /** Compatibility line from snippets/cl-compat.liquid ('short' part) for the products in the look */
  /** @param {any} look */
  #compatLine(look) {
    const lower = (/** @type {string} */ t) => t.charAt(0).toLowerCase() + t.slice(1);
    const band = look.band?.band.compat || '';
    const charm = look.charm?.compat || '';
    if (band && charm) return `Band ${lower(band)} · Claspie ${lower(charm)}`;
    if (charm) return `Claspie ${lower(charm)} (on your original WHOOP band)`;
    // Claspies have no model line (Cris, 14 Oct); a Claspie-only look still says where it goes
    if (!band && look.charm) return 'Fits your original WHOOP band';
    return band;
  }

  /**
   * @param {string} source
   * @param {boolean} [stay] - re-render in place (the Claspie switch): no screen change, no scroll
   */
  #renderResult(source, stay = false) {
    const look = this.#main;
    if (!look) return;
    this.#source = source;
    // The look as it will be bought (band only when the Claspie is switched off)
    const main = this.#shown;
    const set = (/** @type {string} */ key, /** @type {string} */ text) => {
      this.querySelectorAll(`[data-r="${key}"]`).forEach((el) => {
        el.textContent = text;
      });
    };
    const eyebrows = { saved: 'Welcome back · your WHOOP look', link: 'A Claspora look' };
    set('eyebrow', eyebrows[source] || 'Your WHOOP look');
    set('name', main.info.name);
    set('line', main.info.line);

    const copy = this.#fitCopy();
    const note = this.querySelector('[data-r="note"]');
    if (note instanceof HTMLElement) {
      note.textContent = copy.note;
      note.hidden = !copy.note;
    }
    const pickModel = this.querySelector('[data-r="pick-model"]');
    if (pickModel instanceof HTMLElement) pickModel.hidden = Boolean(MODEL_LABELS[this.#answers.model]);
    const fit = this.querySelector('[data-r="fit"]');
    if (fit instanceof HTMLElement) {
      fit.textContent = this.#compatLine(main) || copy.fit;
      fit.hidden = !fit.textContent;
    }
    // WHOOP MG / 4.0 (no band for it yet): optional "tell me when bands arrive" signup, tagged per model
    const notify = this.querySelector('[data-r="notify"]');
    const model = this.#answers.model;
    if (notify instanceof HTMLElement) notify.hidden = !(MODEL_LABELS[model] && !this.#looks.some((l) => l.band));
    const tags = this.querySelector('[data-notify-tags]');
    if (tags instanceof HTMLInputElement && MODEL_LABELS[model]) tags.value = `notify-bands, notify-bands-${String(model).replace('.', '-')}`;

    const visual = this.querySelector('[data-r="visual"]');
    if (visual) {
      visual.innerHTML =
        this.#visualHtml(main) +
        (!main.photo && main.band && main.band.band.place && main.charm?.cutout ? '<p class="cl-quiz__visual-note">Preview made from our product photos.</p>' : '');
      this.#placeStages(visual, [main]);
      // Skeleton until the photos are in (no layout shift: the frame keeps its size)
      const imgs = [...visual.querySelectorAll('img')];
      visual.classList.toggle('is-loading', imgs.some((i) => !i.complete));
      imgs.forEach((i) =>
        i.addEventListener('load', () => visual.classList.toggle('is-loading', imgs.some((x) => !x.complete)), { once: true })
      );
    }

    const parts = this.querySelector('[data-r="parts"]');
    if (parts) {
      const part = (/** @type {string} */ kind, /** @type {string} */ handle, /** @type {string} */ name, /** @type {string} */ url, /** @type {string} */ img, /** @type {number} */ price) => `
        <li><a class="cl-quiz__part" href="${url}" data-product-link data-kind="${kind}" data-handle="${handle}">
          <img src="${img}" alt="" width="56" height="56" loading="lazy">
          <span class="cl-quiz__part-text"><span class="cl-quiz__part-kind">Your ${kind === 'charm' ? 'Claspie' : kind}</span><span class="cl-quiz__part-name">${this.#esc(name)}</span></span>
          <span class="cl-quiz__part-price">${this.#money(price)}</span>
        </a></li>`;
      let html = '';
      if (main.band) {
        const b = main.band;
        html += part('band', b.band.handle, this.#bandName(main), this.#bandUrl(main), b.variant.image, b.variant.price);
      }
      // Band + Claspie: the Claspie is a switch (off = the band alone); otherwise a plain row
      const c = look.charm;
      const all = this.#charmsOf(look);
      if (c && look.band) {
        const on = this.#withCharm;
        const name = all.length > 1 ? this.#charmList(look) : this.#charmName(c);
        const kindLabel = all.length > 1 ? `Add your ${all.length} Claspies` : 'Add your Claspie';
        const extraPrice = all.reduce((sum, x) => sum + x.price, 0);
        const switchLabel = all.length > 1 ? `Add the ${all.length} Claspies` : `Add the ${this.#charmShort(c)} Claspie`;
        html += `
        <li class="cl-quiz__part cl-quiz__part--switch${on ? '' : ' is-off'}">
          <a class="cl-quiz__part-link" href="${c.url}" data-product-link data-kind="charm" data-handle="${c.handle}">
            <img src="${c.image}" alt="" width="56" height="56" loading="lazy">
            <span class="cl-quiz__part-text"><span class="cl-quiz__part-kind">${kindLabel}</span><span class="cl-quiz__part-name${all.length > 1 ? ' cl-quiz__part-name--list' : ''}">${this.#esc(name)}</span></span>
          </a>
          <span class="cl-quiz__part-price">+${this.#money(extraPrice)}</span>
          <button type="button" class="cl-quiz__switch" role="switch" aria-checked="${on}" aria-label="${this.#esc(switchLabel)}" data-charm-toggle>
            <span class="cl-quiz__switch-track"><span class="cl-quiz__switch-knob"></span></span>
          </button>
        </li>`;
      } else if (c) html += part('charm', c.handle, this.#charmName(c), c.url, c.image, c.price);
      parts.innerHTML = html;
    }
    const total = this.#total(main);
    const saving = main.set && main.band && main.charm ? this.#partsTotal(main) - total : 0;
    set('set-label', main.band && main.charm ? (saving > 0 ? `Your set · ${this.#money(saving)} less` : 'Your set') : main.band ? 'Your band' : 'Your Claspie');
    set('set-total', this.#money(total));
    set('add-label', main.band && main.charm ? 'Add my look →' : main.band ? 'Add my band →' : 'Add my Claspie →');
    set('total', this.#money(total));
    set('sticky-name', this.#comboName(main));
    // Free-shipping line for the visitor's market (snippets/cl-shipping.liquid: EU €70 / international €100)
    const threshold = Number(this.dataset.freeShippingCents || 0);
    const word = this.dataset.freeShippingWord || '';
    const ship = this.querySelector('[data-r="ship"]');
    if (ship instanceof HTMLElement) {
      ship.hidden = !threshold;
      ship.textContent = total >= threshold ? `Free ${word}shipping on this look` : `Free ${word}shipping over ${this.#money(threshold)}`;
    }
    set('why', this.#why(main));

    // Pre-order: label + estimated dispatch above the button (snippets/cl-preorder.liquid)
    const pre = this.querySelector('.cl-quiz__details [data-cl-preorder]');
    if (pre instanceof HTMLElement) pre.hidden = !this.#isPreorder(main);

    const cart = this.querySelector('[data-r="cart"]');
    if (cart instanceof HTMLElement) cart.hidden = true;
    this.querySelectorAll('[data-add="main"]').forEach((b) => b.removeAttribute('aria-busy'));

    set('share-label', 'Share my look');
    const error = this.querySelector('[data-r="error"]');
    if (error instanceof HTMLElement) error.hidden = true;

    // "Your other 2 looks": the next best looks for these answers (or your match after a swap).
    // "See look" swaps it in place; answers stay.
    const alts = this.querySelector('[data-r="alts"]');
    const altsBlock = this.querySelector('[data-r="alts-block"]');
    const others = this.#looks.map((l, n) => ({ l, n })).filter(({ n }) => n !== this.#current);
    if (altsBlock instanceof HTMLElement) altsBlock.hidden = !others.length;
    if (alts) {
      alts.innerHTML = others
        .map(
          ({ l, n }) => `
            <div class="cl-quiz__alt">
              <div class="cl-quiz__alt-pics">${this.#visualHtml(l, true)}</div>
              <span class="cl-quiz__alt-label">${this.#esc(l.label)}</span>
              <span class="cl-quiz__alt-names">${this.#esc(this.#comboName(l))}</span>
              <span class="cl-quiz__alt-price">${this.#money(this.#total(l))}</span>
              <button type="button" class="cl-quiz__alt-add" data-see-look="${n}">See look</button>
            </div>`
        )
        .join('');
      this.#placeStages(alts, others.map(({ l }) => l));
    }
    if (!stay) this.#screen('result');
  }

  /** "See this look": that direction becomes the main result; the previous one moves down. */
  /** @param {number} index */
  #selectLook(index) {
    const chosen = this.#looks[index];
    if (!chosen) return;
    this.#current = index;
    this.#withCharm = true;
    clTrack('quiz_alt_selected', { look: chosen.info.name, direction: chosen.label });
    this.#renderResult('swap');
    const result = this.querySelector('[data-screen="result"]');
    if (result instanceof HTMLElement) {
      window.scrollTo({ top: result.getBoundingClientRect().top + window.scrollY - 100, behavior: 'smooth' });
    }
  }

  /* ---------- Story picture ---------- */

  /** @param {string} src @returns {Promise<HTMLImageElement | null>} */
  #loadImage(src) {
    return new Promise((resolve) => {
      if (!src) return resolve(null);
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = () => resolve(null);
      // Bigger version of the same Shopify CDN image
      img.src = new URL(src.replace(/width=\d+/, 'width=1200'), location.href).href;
    });
  }

  /**
   * 1080 x 1920 story: logo, "My look", the look's name and line, band + charm photos and names,
   * and where to find the quiz. Drawn on a canvas from the result; no app, no server.
   * @returns {Promise<Blob | null>}
   */
  async #storyImage() {
    const look = this.#shown;
    if (!look) return null;
    const W = 1080;
    const H = 1920;
    const canvas = document.createElement('canvas');
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    const css = getComputedStyle(document.documentElement);
    const colour = (/** @type {string} */ name, /** @type {string} */ fallback) => css.getPropertyValue(name).trim() || fallback;
    const ink = colour('--ink', '#0E1726');
    const page = colour('--page', '#F7F5F2');
    const accent = colour('--accent-deep', '#8A6A3B');
    const slate = colour('--slate', '#44506A');
    const font = getComputedStyle(document.body).fontFamily || 'sans-serif';
    try {
      await Promise.all([document.fonts.load(`600 100px ${font}`), document.fonts.load(`400 40px ${font}`)]);
    } catch (e) {}

    const a = this.#answers;
    const info = this.#lookInfo();
    const [bandImg, charmImg] = await Promise.all([
      this.#loadImage(look.band ? look.band.variant.image : ''),
      this.#loadImage(look.charm ? look.charm.image : ''),
    ]);

    ctx.fillStyle = page;
    ctx.fillRect(0, 0, W, H);

    // Logo: ring mark (ink, gold closing segment) + wordmark
    // Instagram covers roughly the top and bottom 250px of a story, so content stays in between
    const logoY = 280;
    ctx.lineWidth = 11;
    ctx.lineCap = 'butt';
    const cx = W / 2 - 150;
    ctx.strokeStyle = ink;
    ctx.beginPath();
    ctx.arc(cx, logoY, 31, (40 * Math.PI) / 180, (320 * Math.PI) / 180);
    ctx.stroke();
    ctx.strokeStyle = '#C4A06A';
    ctx.beginPath();
    ctx.arc(cx, logoY, 31, (-40 * Math.PI) / 180, (40 * Math.PI) / 180);
    ctx.stroke();
    ctx.fillStyle = ink;
    ctx.font = `700 64px ${font}`;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillText('Claspora', cx + 58, logoY + 2);

    // Heading
    ctx.textAlign = 'center';
    ctx.textBaseline = 'alphabetic';
    ctx.fillStyle = accent;
    ctx.font = `600 34px ${font}`;
    if ('letterSpacing' in ctx) ctx.letterSpacing = '6px';
    ctx.fillText('MY LOOK', W / 2, 430);
    if ('letterSpacing' in ctx) ctx.letterSpacing = '-3px';
    ctx.fillStyle = ink;
    ctx.font = `600 104px ${font}`;
    ctx.fillText(info.name, W / 2, 545, W - 120);
    if ('letterSpacing' in ctx) ctx.letterSpacing = '0px';
    ctx.fillStyle = slate;
    ctx.font = `400 38px ${font}`;
    this.#wrap(ctx, info.line, W / 2, 620, W - 220, 50);

    // Photo cards
    const card = (/** @type {HTMLImageElement | null} */ img, /** @type {number} */ x, /** @type {number} */ y, /** @type {number} */ size, /** @type {number} */ radius, zoom = 0.92) => {
      ctx.save();
      ctx.shadowColor = 'rgba(14, 23, 38, 0.10)';
      ctx.shadowBlur = 50;
      ctx.shadowOffsetY = 18;
      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.roundRect(x, y, size, size, radius);
      ctx.fill();
      ctx.restore();
      if (img) {
        ctx.save();
        ctx.beginPath();
        ctx.roundRect(x, y, size, size, radius);
        ctx.clip();
        const scale = Math.min(size / img.width, size / img.height) * zoom;
        const w = img.width * scale;
        const h = img.height * scale;
        ctx.drawImage(img, x + (size - w) / 2, y + (size - h) / 2, w, h);
        ctx.restore();
      }
    };
    let namesY;
    if (look.band && look.charm) {
      card(bandImg, 170, 730, 600, 44);
      card(charmImg, 620, 1080, 320, 36, 1.45); // supplier charm photos have wide white margins
      namesY = 1475;
    } else if (look.band) {
      card(bandImg, 220, 740, 640, 48);
      namesY = 1470;
    } else {
      card(charmImg, 220, 740, 640, 48, 1.45);
      namesY = 1470;
    }

    // Names
    ctx.fillStyle = ink;
    ctx.font = `600 40px ${font}`;
    const names = this.#names(look);
    names.forEach((n, i) => ctx.fillText(i ? `+ ${n}` : n, W / 2, namesY + i * 56, W - 160));

    // Footer: where to find it
    const pillW = 640;
    const pillY = 1560;
    ctx.fillStyle = ink;
    ctx.beginPath();
    ctx.roundRect((W - pillW) / 2, pillY, pillW, 100, 50);
    ctx.fill();
    ctx.fillStyle = page;
    ctx.font = `500 38px ${font}`;
    // Store's primary domain from Shopify (data-shop-domain on <cl-quiz>), never hard-coded
    const domain = (this.dataset.shopDomain || location.hostname).replace(/^www\./, '');
    ctx.fillText(`Find yours: ${domain}/find`, W / 2, pillY + 63, pillW - 60);
    ctx.fillStyle = slate;
    ctx.font = `400 28px ${font}`;
    ctx.fillText('@wearclaspora', W / 2, 1705);

    return new Promise((resolve) => canvas.toBlob((b) => resolve(b), 'image/png'));
  }

  /**
   * @param {CanvasRenderingContext2D} ctx
   * @param {string} text
   * @param {number} x
   * @param {number} y
   * @param {number} maxWidth
   * @param {number} lineHeight
   */
  #wrap(ctx, text, x, y, maxWidth, lineHeight) {
    const words = text.split(' ');
    let line = '';
    let row = 0;
    for (const word of words) {
      const test = line ? `${line} ${word}` : word;
      if (ctx.measureText(test).width > maxWidth && line) {
        ctx.fillText(line, x, y + row * lineHeight);
        line = word;
        row += 1;
      } else {
        line = test;
      }
    }
    if (line) ctx.fillText(line, x, y + row * lineHeight);
  }

  /** @param {HTMLElement} button */
  async #share(button) {
    if (button.getAttribute('aria-busy') === 'true') return;
    button.setAttribute('aria-busy', 'true');
    const label = button.querySelector('[data-r="share-label"]');
    try {
      const blob = await this.#storyImage();
      if (!blob) throw new Error('No image');
      const name = (this.#lookInfo().name || 'my-look').toLowerCase().replace(/[^a-z]+/g, '-');
      const file = new File([blob], `clasplab-${name}.png`, { type: 'image/png' });
      const link = `${window.location.origin}/pages/find-your-look?look=${this.#encode()}`;
      const text = `My WHOOP look: ${this.#lookInfo().name}. See it and find yours: ${link}`;
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], text });
        clTrack('quiz_shared', { method: 'share', look: this.#lookInfo().name });
      } else {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = file.name;
        document.body.append(link);
        link.click();
        link.remove();
        window.setTimeout(() => URL.revokeObjectURL(url), 4000);
        if (label) label.textContent = 'Saved. Post it to your story';
        clTrack('quiz_shared', { method: 'download', look: this.#lookInfo().name });
      }
    } catch (e) {
      // A cancelled share sheet throws AbortError: nothing to report
      if (!(e instanceof DOMException && e.name === 'AbortError')) {
        console.error(e);
        if (label) label.textContent = 'Could not make the picture';
      }
    } finally {
      button.removeAttribute('aria-busy');
    }
  }

  /** Debug helper for the preview: returns the story as a data URL */
  async storyPreview() {
    const blob = await this.#storyImage();
    if (!blob) return '';
    return new Promise((resolve) => {
      const r = new FileReader();
      r.onload = () => resolve(String(r.result));
      r.readAsDataURL(blob);
    });
  }

  /* ---------- Cart ---------- */

  /**
   * @param {number} index
   * @param {HTMLElement} button
   */
  async #add(index, button) {
    const look = index === this.#current ? this.#shown : this.#looks[index];
    if (!look || this.#busy) return;
    this.#busy = true;
    button.setAttribute('aria-busy', 'true');
    const error = this.querySelector('[data-r="error"]');
    if (error instanceof HTMLElement) error.hidden = true;

    // @ts-ignore set by snippets/cl-preorder.liquid when pre-orders are on
    const preorder = window.ClaspLabPreorder;
    const line = (/** @type {any} */ item, /** @type {boolean} */ isPre) => (preorder ? preorder.line(item, isPre) : item);
    // Charm first: the cart lists the newest line on top, so the band leads the look in the cart
    const items = [];
    if (look.set && look.band && look.charm) {
      // The look is a live set: one line at the set price
      items.push(line({ id: look.set.id, quantity: 1 }, Boolean(look.set.preorder)));
    } else {
      this.#charmsOf(look).forEach((c) => items.push(line({ id: c.id, quantity: 1 }, Boolean(c.preorder))));
      if (look.band) items.push(line({ id: look.band.variant.id, quantity: 1 }, Boolean(look.band.band.preorder)));
    }
    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
      .filter(Boolean);
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity })),
        promise: deferred.promise,
      })
    );
    try {
      // @ts-ignore Theme is a global set by the theme's scripts snippet
      const routes = Theme.routes;
      const response = await fetch(routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      }).then((r) => r.json());
      const cart = await fetch(`${routes.cart_url}.json`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
      const didError = Boolean(response.status);
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'cl-quiz',
          sourceId: 'cl-quiz',
          itemCount: items.length,
          sections: response.sections,
          didError,
        },
      });
      if (didError) {
        this.dispatchEvent(new CartErrorEvent({ error: response.message || 'Add to cart failed', code: 'INVALID' }));
        if (error instanceof HTMLElement) {
          error.textContent = response.description || response.message || 'Could not add to cart.';
          error.hidden = false;
        }
      } else {
        const link = this.querySelector('[data-r="cart"]');
        if (link instanceof HTMLElement && index === this.#current) link.hidden = false;
        // Main look: the button and the phone's sticky bar both say "Added"
        this.querySelectorAll(index === this.#current ? '[data-add="main"]' : `[data-add="${index}"]`).forEach((b) => {
          const label = b.querySelector('[data-r="add-label"]');
          if (label) label.textContent = 'Added ✓';
          else b.textContent = 'Added ✓';
        });
        clTrack('quiz_look_added', {
          look: this.#lookInfo().name,
          option: index,
          items: this.#names(look).join(' + '),
          value: this.#total(look) / 100,
          currency: this.dataset.currency || 'EUR',
        });
      }
    } catch (e) {
      console.error(e);
      deferred.reject(e);
      if (error instanceof HTMLElement) {
        error.textContent = 'Could not add to cart. Please try again.';
        error.hidden = false;
      }
    } finally {
      this.#busy = false;
      button.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('cl-quiz')) customElements.define('cl-quiz', ClaspLabQuiz);
